// Recovering a document that outlived its assets.
//
// Every page is its own hashed chunk (Vite code-splits `createInertiaApp`'s
// `pages` glob), and each deploy ships a fresh image with fresh hashes — the
// old chunks are gone, not archived. So a document a browser has been holding
// since an earlier release asks for a chunk the server no longer has, the
// dynamic import 404s, and Inertia never mounts.
//
// WHAT THAT ACTUALLY LOOKS LIKE — corrected 2026-08-17, having said the wrong
// thing here since this file was written. This comment used to claim `#app`
// stays empty and the visitor gets a BLANK PAGE. It does not. Production
// renders server-side, so the document — stale or not — arrives with ~26 KB of
// markup already inside `#app`, and a mount failure means React never touches
// it. Verified with JavaScript disabled: `/` renders completely, headline, 24
// nav links, 2,532 characters of text. The page looks perfect.
//
// It is worse than blank, because it is silent. Nothing that needs JS works,
// and the enquiry form is the one that counts: it carries no `action`, no
// `method` and no `authenticity_token` (it posts via `useForm`/XHR), so a
// visitor can fill it in, press Send enquiry, and NOTHING HAPPENS — no error,
// no submission, no Sentry event, no enquiry. Ordinary <a href> links still
// work as full page loads, so nothing signals to them that anything is wrong.
// That is what this reload is really protecting, and why it is worth a reload
// rather than leaving the page as it is.
//
// Real traffic hit exactly that (Sentry, 2026-07-28): Mobile Safari on the
// homepage raised `TypeError: Importing a module script failed.` while running
// release 610b83f — thirty-one commits behind what was deployed. iOS holds
// tabs for days, so this is ordinary behaviour, not an edge case.
//
// NOT EVERY OCCURRENCE IS STALENESS. Sentry, 2026-08-17: a headless client
// failed to fetch `home-DvhrU-E1.js` while running the CURRENT release, and
// that chunk was serving 200 at the time. A dead network drops a chunk that
// exists just as surely as a deploy removes one. The reload is right either
// way; the diagnosis is not the same, so check whether the asset actually
// 404s before concluding a deploy ate it.
//
// Vite wraps every split import in `__vitePreload`, which dispatches a
// cancellable `vite:preloadError` before rethrowing. Cancelling it and
// reloading swaps the stale document for the current one, which is the only
// thing that can fix this from the client.
const RELOAD_MARKER = 'jx:stale-deploy-reload'

// A reload that doesn't fix it must not reload again — that would be an
// infinite loop against a genuinely broken deploy, and it would bury the
// evidence. Recent means "we already tried this, and here we still are"; an
// unrelated occurrence hours later gets its own attempt.
const RETRY_WINDOW_MS = 10_000

function reloadedRecently(): boolean {
  try {
    const previous = window.sessionStorage.getItem(RELOAD_MARKER)
    return previous !== null && Date.now() - Number(previous) < RETRY_WINDOW_MS
  } catch {
    // Private mode / storage disabled: treat it as "not yet tried". A reload
    // loop is the worse failure, but we can't detect one without storage, and
    // never reloading means a guaranteed blank page.
    return false
  }
}

export function recoverFromStaleDeploy(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', (event) => {
    if (reloadedRecently()) {
      // Let it through to Sentry: a chunk that's still missing after a fresh
      // document is a real fault, and this is the one signal that says so.
      //
      // So a Sentry `Failed to fetch dynamically imported module` is the
      // EXPECTED end state of a client that fails twice, not evidence this
      // recovery is broken. Read it as "we reloaded and it still failed".
      return
    }

    try {
      window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()))
    } catch {
      // Storage is optional here; the reload below is the part that matters.
    }

    // Stops Vite rethrowing, so a recoverable staleness doesn't also land in
    // Sentry as an unhandled rejection.
    event.preventDefault()
    window.location.reload()
  })
}
